/**
 * Engine hit signal — fixed-shape combat memory for AI perception.
 * "Am I being hit, by whom, how hard lately?" Domain owns what to do with
 * the answer (survival rules, heal targeting); this kit only records/decays.
 * Deliberately NOT a generic blackboard — one typed record keeps ticks flat.
 */

export type HitSignal = {
  lastHitAt: number;
  /** -1 = none / non-unit source (boss = domain's choice of sentinel). */
  lastAttackerId: number;
  /** Exponentially decayed damage accumulator (see decayedDamage). */
  recentDamage: number;
  lastDecayAt: number;
};

export function createHitSignal(): HitSignal {
  return {
    lastHitAt: Number.NEGATIVE_INFINITY,
    lastAttackerId: -1,
    recentDamage: 0,
    lastDecayAt: 0,
  };
}

/** Record a hit at `time`; decays the accumulator up to `time` first. */
export function noteHit(
  sig: HitSignal,
  dmg: number,
  attackerId: number,
  time: number,
  halfLifeSec = 2.5,
): void {
  sig.recentDamage = decayedDamage(sig, time, halfLifeSec);
  sig.lastDecayAt = time;
  sig.recentDamage += Math.max(0, dmg);
  sig.lastHitAt = time;
  sig.lastAttackerId = attackerId;
}

/** Accumulator value at `time` (pure read — does not mutate). */
export function decayedDamage(
  sig: HitSignal,
  time: number,
  halfLifeSec = 2.5,
): number {
  const dt = Math.max(0, time - sig.lastDecayAt);
  if (sig.recentDamage <= 0) return 0;
  return sig.recentDamage * Math.pow(0.5, dt / Math.max(0.01, halfLifeSec));
}

/** True when hit within the trailing window. */
export function underPressure(
  sig: HitSignal,
  time: number,
  windowSec = 1.5,
): boolean {
  return time - sig.lastHitAt <= windowSec;
}
